import { useState, useEffect } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { formatCurrency, parseCurrency } from '@/lib/types/job';
import { Badge } from '@/components/ui/badge';
import { BidLineItemInput } from '@/hooks/useBidLineItems';
import { usePlatformSetting } from '@/hooks/usePlatformSettings';
import { useUserRole } from '@/hooks/useUserRole';
import { RFQQuantityOption } from '@/hooks/useRFQQuantityOptions';
import { useVendorLocations, VendorLocation } from '@/hooks/useVendorLocations';
import { useVendorBidTax, TaxCalculation } from '@/hooks/useVendorBidTax';
import { TaxBreakdownTable } from './TaxBreakdownTable';
import { useAuth } from '@/lib/auth';
import { MapPin, AlertCircle, Calculator, Loader2 } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';

interface RFQSpecification {
  id: string;
  specification_id: string;
  specifications: {
    name: string;
  };
}

interface BidLineItemsTableProps {
  specifications: RFQSpecification[];
  quantityOptions: RFQQuantityOption[];
  lineItems: BidLineItemInput[];
  onChange: (lineItems: BidLineItemInput[]) => void;
  rfqId: string;
  taxCalculations?: TaxCalculation[];
  onTaxCalculationsChange?: (calculations: TaxCalculation[]) => void;
  totalVendorTax?: number;
  onTotalVendorTaxChange?: (amount: number) => void;
}

export const BidLineItemsTable = ({ 
  specifications, 
  quantityOptions, 
  lineItems,
  onChange,
  rfqId,
  taxCalculations: externalTaxCalculations,
  onTaxCalculationsChange,
  totalVendorTax: externalTotalVendorTax,
  onTotalVendorTaxChange,
}: BidLineItemsTableProps) => {
  const { user } = useAuth();
  const { isVendor } = useUserRole();
  const { data: platformFeeSetting } = usePlatformSetting('platform_fee_percentage');
  const { locations, primaryLocation, loading: locationsLoading } = useVendorLocations(user?.id);
  
  const [priceInputs, setPriceInputs] = useState<Record<string, string>>({});
  const [leadTimeInputs, setLeadTimeInputs] = useState<Record<string, string>>({});

  // Tax calculation hook
  const {
    taxEnabled,
    taxEnabledLoading,
    taxCalculations: internalTaxCalcs,
    isCalculating,
    hasCalculated,
    error: taxError,
    calculateTax,
    updateTaxAmount,
    resetTaxAmount,
    resetAllTaxAmounts,
    setAllToZero,
    getTotalVendorTax,
    getTotalCalculatedTax,
    hasEdits: hasTaxEdits,
    clearCalculations,
  } = useVendorBidTax(rfqId);

  // Use external tax state if provided, otherwise use internal
  const taxCalculations = externalTaxCalculations ?? internalTaxCalcs;

  const feePercentage = parseFloat(platformFeeSetting?.setting_value || '10');

  const calculateVendorPayout = (totalPriceCents: number): number => {
    const feeAmount = Math.floor(totalPriceCents * (feePercentage / 100));
    return totalPriceCents - feeAmount;
  };

  // Handle tax calculation
  const handleCalculateTax = async () => {
    if (lineItems.length === 0) {
      toast.error('Please enter pricing for at least one item');
      return;
    }

    const incompleteItems = lineItems.filter(li => li.unit_price_cents <= 0);
    if (incompleteItems.length > 0) {
      toast.error('Please enter pricing for all items before calculating tax');
      return;
    }

    const result = await calculateTax(lineItems);
    
    if (result.success) {
      toast.success(`Tax calculated: ${formatCurrency(result.totalTaxCents)}`);
      // Notify parent if callback provided
      if (onTaxCalculationsChange) {
        onTaxCalculationsChange(result.calculations);
      }
      if (onTotalVendorTaxChange) {
        onTotalVendorTaxChange(result.totalTaxCents);
      }
    } else if (result.error) {
      toast.error(result.error);
    }
  };

  // Update parent when tax changes
  const handleTaxEdit = (lineItemKey: string, newAmountCents: number) => {
    updateTaxAmount(lineItemKey, newAmountCents);
    
    // Build the updated calculations array
    const updatedCalculations = taxCalculations.map(calc =>
      calc.lineItemKey === lineItemKey
        ? { ...calc, vendorTaxCents: newAmountCents, isEdited: true }
        : calc
    );
    
    // Notify parent of updated calculations array
    if (onTaxCalculationsChange) {
      onTaxCalculationsChange(updatedCalculations);
    }
    
    // Recalculate total and notify parent
    if (onTotalVendorTaxChange) {
      const newTotal = updatedCalculations.reduce((sum, calc) => sum + calc.vendorTaxCents, 0);
      onTotalVendorTaxChange(newTotal);
    }
  };

  // Handle setting all taxes to zero (for no-nexus scenarios)
  const handleSetAllToZero = () => {
    setAllToZero();
    
    // Build the updated calculations array with all zeros
    const updatedCalculations = taxCalculations.map(calc => ({
      ...calc,
      vendorTaxCents: 0,
      isEdited: true,
    }));
    
    // Notify parent of updated calculations array
    if (onTaxCalculationsChange) {
      onTaxCalculationsChange(updatedCalculations);
    }
    
    // Notify parent of zero total
    if (onTotalVendorTaxChange) {
      onTotalVendorTaxChange(0);
    }
  };

  // Group quantity options by specification
  const optionsBySpec = quantityOptions.reduce((acc, opt) => {
    const specId = opt.specification_id;
    if (!acc[specId]) {
      acc[specId] = [];
    }
    acc[specId].push(opt);
    return acc;
  }, {} as Record<string, RFQQuantityOption[]>);

  const getLineItemKey = (specId: string, optionIndex: number) => 
    `${specId}_${optionIndex}`;

  const getLineItem = (specId: string, optionIndex: number): BidLineItemInput | undefined =>
    lineItems.find(li => 
      li.specification_id === specId && 
      li.quantity_option === optionIndex
    );

  const handleUnitPriceChange = (
    specId: string, 
    optionIndex: number,
    qtyOption: RFQQuantityOption,
    value: string
  ) => {
    const key = getLineItemKey(specId, optionIndex);
    setPriceInputs(prev => ({ ...prev, [key]: value }));
    
    const unitPriceCents = parseCurrency(value);
    const totalPriceCents = unitPriceCents * qtyOption.quantity_option;

    const updatedItems = [...lineItems];
    const existingIndex = updatedItems.findIndex(li => 
      li.specification_id === specId && 
      li.quantity_option === optionIndex
    );

    const existingItem = existingIndex >= 0 ? updatedItems[existingIndex] : undefined;
    
    const newItem: BidLineItemInput = {
      specification_id: specId,
      quantity_option: optionIndex,
      unit_price_cents: unitPriceCents,
      total_price_cents: totalPriceCents,
      lead_time_days: existingItem?.lead_time_days ?? undefined as any,
      notes: existingItem?.notes ?? '',
      origin_location_id: existingItem?.origin_location_id ?? primaryLocation?.id,
    };

    if (existingIndex >= 0) {
      updatedItems[existingIndex] = newItem;
    } else {
      updatedItems.push(newItem);
    }

    onChange(updatedItems);

    // Invalidate tax calculations when prices change (if tax was already calculated)
    if (hasCalculated) {
      clearCalculations();
      if (onTaxCalculationsChange) {
        onTaxCalculationsChange([]);
      }
      if (onTotalVendorTaxChange) {
        onTotalVendorTaxChange(0);
      }
    }
  };

  const handleLeadTimeChange = (
    specId: string,
    optionIndex: number,
    value: string
  ) => {
    const key = getLineItemKey(specId, optionIndex);
    setLeadTimeInputs(prev => ({ ...prev, [key]: value }));
    
    const numValue = value === '' ? undefined : parseInt(value);
    const updatedItems = lineItems.map(li => {
      if (li.specification_id === specId && li.quantity_option === optionIndex) {
        return { ...li, lead_time_days: numValue as any };
      }
      return li;
    });
    onChange(updatedItems);
  };

  const handleLeadTimeBlur = (
    specId: string,
    optionIndex: number,
    value: string
  ) => {
    const key = getLineItemKey(specId, optionIndex);
    const numValue = parseInt(value);
    if (isNaN(numValue) || numValue < 1) {
      setLeadTimeInputs(prev => ({ ...prev, [key]: '1' }));
      handleLeadTimeChange(specId, optionIndex, '1');
    }
  };

  const handleOriginLocationChange = (
    specId: string,
    optionIndex: number,
    locationId: string
  ) => {
    const updatedItems = lineItems.map(li => {
      if (li.specification_id === specId && li.quantity_option === optionIndex) {
        return { ...li, origin_location_id: locationId };
      }
      return li;
    });

    // If item doesn't exist yet, create it with the location
    const exists = updatedItems.some(li => 
      li.specification_id === specId && li.quantity_option === optionIndex
    );

    if (!exists) {
      updatedItems.push({
        specification_id: specId,
        quantity_option: optionIndex,
        unit_price_cents: 0,
        total_price_cents: 0,
        origin_location_id: locationId,
      });
    }

    onChange(updatedItems);
  };

  const isComplete = () => {
    const expectedCount = specifications.reduce((count, spec) => {
      const opts = optionsBySpec[spec.specification_id] || [];
      return count + opts.length;
    }, 0);
    return lineItems.length === expectedCount && 
           lineItems.every(li => li.unit_price_cents > 0 && (li.lead_time_days || 0) > 0);
  };

  const getLocationDisplay = (location: VendorLocation) => {
    return `${location.location_name || 'Unnamed'} - ${location.city}, ${location.state}`;
  };

  const hasLocations = locations.length > 0;
  const showLocationColumn = isVendor && hasLocations;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Bid Pricing Matrix</CardTitle>
        <CardDescription>
          Provide unit pricing for each specification and quantity option
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isVendor && !locationsLoading && !hasLocations && (
          <Alert className="mb-4">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              <span className="font-medium">No fulfillment locations registered.</span>{' '}
              <a href="/profile" className="text-primary underline">Add a location</a> in your Company Profile to specify where items will be produced.
            </AlertDescription>
          </Alert>
        )}

        <div className="space-y-6">
          {specifications.map((spec) => {
            const options = optionsBySpec[spec.specification_id] || [];
            if (options.length === 0) return null;

            return (
              <div key={spec.id} className="space-y-3">
                <h4 className="font-semibold text-sm">{spec.specifications.name}</h4>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-32">Quantity</TableHead>
                      <TableHead className="w-40">Unit Price *</TableHead>
                      <TableHead className="w-32">Total Price</TableHead>
                      {isVendor && (
                        <TableHead className="w-36">Your Payout</TableHead>
                      )}
                      <TableHead className="w-32">Lead Time (days) *</TableHead>
                      {showLocationColumn && (
                        <TableHead className="w-48">
                          <div className="flex items-center gap-1">
                            <MapPin className="h-4 w-4" />
                            Fulfillment Location
                          </div>
                        </TableHead>
                      )}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {options.map((option) => {
                      const lineItem = getLineItem(spec.specification_id, option.quantity_option);
                      const key = getLineItemKey(spec.specification_id, option.quantity_option);
                      const priceInput = priceInputs[key] || '';
                      const totalPrice = lineItem?.total_price_cents || 0;
                      const selectedLocationId = lineItem?.origin_location_id || primaryLocation?.id || '';

                      return (
                        <TableRow key={`${spec.id}-${option.quantity_option}`}>
                          <TableCell>
                            <div className="space-y-1">
                              <div className="font-medium">
                                {option.quantity_option.toLocaleString()} {option.unit}
                              </div>
                              {option.notes && (
                                <div className="text-xs text-muted-foreground">
                                  {option.notes}
                                </div>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Input
                              type="text"
                              placeholder="$0.00"
                              value={priceInput}
                              onChange={(e) => handleUnitPriceChange(
                                spec.specification_id, 
                                option.quantity_option,
                                option,
                                e.target.value
                              )}
                              className="w-full"
                            />
                          </TableCell>
                          <TableCell>
                            <div className="font-medium text-sm">
                              {formatCurrency(totalPrice)}
                            </div>
                          </TableCell>
                          {isVendor && (
                            <TableCell>
                              <div className="font-medium text-sm text-green-600">
                                {formatCurrency(calculateVendorPayout(totalPrice))}
                              </div>
                              {totalPrice > 0 && (
                                <div className="text-xs text-muted-foreground">
                                  After {feePercentage}% fee
                                </div>
                              )}
                            </TableCell>
                          )}
                          <TableCell>
                            <Input
                              type="number"
                              min="1"
                              placeholder="Days"
                              value={leadTimeInputs[getLineItemKey(spec.specification_id, option.quantity_option)] ?? (lineItem?.lead_time_days || '')}
                              onChange={(e) => handleLeadTimeChange(
                                spec.specification_id,
                                option.quantity_option,
                                e.target.value
                              )}
                              onBlur={(e) => handleLeadTimeBlur(
                                spec.specification_id,
                                option.quantity_option,
                                e.target.value
                              )}
                              className="w-24"
                            />
                          </TableCell>
                          {showLocationColumn && (
                            <TableCell>
                              <Select
                                value={selectedLocationId}
                                onValueChange={(value) => handleOriginLocationChange(
                                  spec.specification_id,
                                  option.quantity_option,
                                  value
                                )}
                              >
                                <SelectTrigger className="w-full">
                                  <SelectValue placeholder="Select location" />
                                </SelectTrigger>
                                <SelectContent>
                                  {locations.map((loc) => (
                                    <SelectItem key={loc.id} value={loc.id}>
                                      {getLocationDisplay(loc)}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </TableCell>
                          )}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            );
          })}

          <div className="flex items-center gap-2 pt-4 border-t">
            <Label>Status:</Label>
            <Badge variant={isComplete() ? 'default' : 'secondary'}>
              {isComplete() ? 'Complete' : 'Incomplete'}
            </Badge>
          </div>

          {/* Tax Calculation Section */}
          {isVendor && taxEnabled && !taxEnabledLoading && (
            <div className="mt-6 pt-6 border-t space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium flex items-center gap-2">
                    <Calculator className="h-4 w-4" />
                    Estimated Sales Tax
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    Calculate tax based on delivery locations
                  </p>
                </div>
                <Button
                  onClick={handleCalculateTax}
                  disabled={isCalculating || !isComplete()}
                  variant="outline"
                >
                  {isCalculating ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Calculating...
                    </>
                  ) : (
                    <>
                      <Calculator className="h-4 w-4 mr-2" />
                      Calculate Tax
                    </>
                  )}
                </Button>
              </div>

              {taxError && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{taxError}</AlertDescription>
                </Alert>
              )}

              {hasCalculated && taxCalculations.length > 0 && (
                <TaxBreakdownTable
                  calculations={taxCalculations}
                  specifications={specifications.map(s => ({ 
                    id: s.specification_id, 
                    name: s.specifications.name 
                  }))}
                  onTaxEdit={handleTaxEdit}
                  onResetTax={resetTaxAmount}
                  onResetAllTax={resetAllTaxAmounts}
                  onSetAllToZero={handleSetAllToZero}
                  totalVendorTax={getTotalVendorTax()}
                  totalCalculatedTax={getTotalCalculatedTax()}
                  hasEdits={hasTaxEdits()}
                  isLoading={isCalculating}
                />
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
